import { useContext, useEffect, useRef, useState } from 'react';
import { Group } from 'three';

import { isDefined } from '../../../../utils/variables';
import { sfmViewerColors } from '../../../../viewer/elements/design/colors';
import { GeodataImagesContext } from '../geodata-images-state';
import { AlignerContext } from './aligner-state';
import {
  CameraGeometry,
  createCameraGeometry,
  ensureCameraGeometryCapacity,
  getCameraPointColorRGB,
  hideCameraGeometryIndex,
  updateCameraGeometry,
} from './geodata-camera-utils';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';

export function useGeodataCameras(): {
  cameraGroup?: Group;
} {
  const { origo, getImageLocalPosition } = useContext(GeodataSpatialReferenceContext);
  const { legendsMode } = useContext(AlignerContext);
  const { images } = useContext(GeodataImagesContext);
  const [cameraGroup, setCameraGroup] = useState<Group>();

  // Persistent refs so we don't recreate GPU objects on every change
  const geometryRef = useRef<CameraGeometry | null>(null);
  const indexMapRef = useRef<Map<string, number>>(new Map()); // imageId -> slot index
  const freeListRef = useRef<number[]>([]); // reusable slots

  // Initialize once
  useEffect(() => {
    if (!isDefined(origo)) {
      geometryRef.current = null;
      setCameraGroup(undefined);
      return;
    }

    if (!geometryRef.current) {
      const initialCap = Math.max(2048, Math.ceil(images.length * 1.25));
      const geometry = createCameraGeometry(initialCap);

      indexMapRef.current.clear();
      freeListRef.current.length = 0;

      geometryRef.current = geometry;
      setCameraGroup(geometry.group);
    }
  }, [images.length, origo]);

  // Diff & apply updates
  useEffect(() => {
    const geometry = geometryRef.current;
    if (!geometry || !isDefined(origo) || !isDefined(getImageLocalPosition)) {
      return;
    }

    const idxMap = indexMapRef.current;
    const free = freeListRef.current;

    // 1) Remove: free slots for images that no longer exist or lost transform
    for (const [id, idx] of idxMap.entries()) {
      const img = images.find((x) => x.imageIdentifier === id);
      if (!img || !isDefined(img.transform)) {
        // Mark slot as free.
        free.push(idx);
        idxMap.delete(id);
        hideCameraGeometryIndex(geometry, idx);
      }
    }

    // 2) Ensure capacity if we'll add many
    const toAdd = images.filter(
      (img) => isDefined(img.transform) && !idxMap.has(img.imageIdentifier),
    );
    const needed = idxMap.size + toAdd.length;

    const updatedGeometry = ensureCameraGeometryCapacity(geometry, needed);
    if (
      updatedGeometry.camerasCapacity !== geometry.camerasCapacity ||
      updatedGeometry.arrowsCapacity !== geometry.arrowsCapacity ||
      updatedGeometry.arrowheadsCapacity !== geometry.arrowheadsCapacity
    ) {
      geometryRef.current = updatedGeometry;
    }

    // Update local reference to use potentially new geometry after capacity changes
    const currentGeometry = geometryRef.current;
    if (!currentGeometry) {
      return;
    }

    // 3) Upsert all incoming images with transforms.
    // First, assign slots for all images with transforms
    for (const img of images) {
      if (isDefined(img.transform)) {
        let idx = idxMap.get(img.imageIdentifier);
        if (idx === undefined) {
          idx = free.length ? free.pop()! : idxMap.size;
          idxMap.set(img.imageIdentifier, idx);
        }
      }
    }

    // Then update the geometry buffers
    const didStructuralChange = toAdd.length > 0 || free.length > 0;
    updateCameraGeometry({
      images,
      geometry: currentGeometry,
      getPointIndex: (img) => idxMap.get(img.imageIdentifier)!,
      getPosition: (img) => getImageLocalPosition(img.transform!),
      getColor: (img) => getCameraPointColorRGB(img, sfmViewerColors.cameras, legendsMode),
      getArrowIndex: (img, pointIdx) => pointIdx,
      getTransform: (img) => img.transform,
      shouldProcessImage: (img) => isDefined(img.transform),
      hasStructuralChange: didStructuralChange,
    });

    setCameraGroup(currentGeometry.group);
  }, [images, origo, legendsMode, getImageLocalPosition]);

  return { cameraGroup };
}
